import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Chip, Table, Typography } from '@weareopenr/component-library';
import { createColumnHelper } from '@tanstack/react-table';
import ActionsMenu from 'src/components/ActionsMenu';
import createAddressSummary from 'src/pages/Venue/createAddressSummary';
import { getPublishedMenusFormattedData } from 'src/pages/Venues/mapVenuesToTable/mapVenuesToTable';
import { IVenue, VenuesTabProps } from './types';
import styles from './VenuesTab.module.scss';

const columnHelper = createColumnHelper<IVenue>();

const renderActionsCell = (
  venueId: string,
  onView: (venueId: string) => void,
  handleOpenDeletePrompt: (id: string) => void,
) => {
  return <ActionsMenu onView={() => onView(venueId)} onDelete={() => handleOpenDeletePrompt(venueId)} />;
};

const renderNameCell = (name: string, isTrial: boolean) => {
  return (
    <div className={styles['name-container']}>
      <Typography>{name}</Typography>
      {isTrial && <Chip label="Trial" backgroundColour="grape" />}
    </div>
  );
};

const VenuesTab = ({ venues, handleOpenDeletePrompt }: VenuesTabProps) => {
  const navigate = useNavigate();

  const venuesData: IVenue[] = useMemo(
    () =>
      (venues ?? [])
        .map((venue) => ({
          venueId: venue.venue_id,
          name: venue.name,
          isTrial: venue.is_trial,
          creationDate: venue.creation_date,
          address: createAddressSummary(venue.addresses[0]),
          publishedChannels: venue.published_channels,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [venues],
  );

  const onView = useCallback(
    (venueId: string): void => {
      navigate(`/venues/${venueId}`);
    },
    [navigate],
  );

  const handleRowClick = useCallback(
    (rowData: IVenue) => {
      return () => onView(rowData.venueId);
    },
    [onView],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => renderNameCell(info.getValue(), info.row.original.isTrial),
      }),
      columnHelper.accessor('address', {
        header: 'Address',
      }),
      columnHelper.accessor('publishedChannels', {
        header: 'Menus per channel',
        cell: (info) => getPublishedMenusFormattedData(info.getValue() || []),
      }),
      columnHelper.display({
        id: 'actions',
        cell: (info) => renderActionsCell(info.row.original.venueId, onView, handleOpenDeletePrompt),
        size: 80,
      }),
    ],
    [handleOpenDeletePrompt, onView],
  );

  return (
    <Table
      className={styles['table-fill-height']}
      columns={columns}
      data={venuesData}
      isRowClickable={handleRowClick}
    />
  );
};

export default VenuesTab;
